import React, { useState } from 'react'
import { useAuth } from '@/auth/AuthContext'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  Button,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import Logo from '@/assets/AppLogo.png'
import LoginLightMode from '@/assets/LoginLightMode.svg'
import LoginDarkMode from '@/assets/LoginDarkMode.svg'
import ThemeToggle from '@/components/sidebar/ThemeToggle'
import { useAppTheme } from '@/theme/ThemeContext'
import LoginInput from '@/components/auth/LoginInput'
import { useSitesStore } from '@/store/sitesStore'
import { resolvePostAuthDestination } from '@/services/sitesService'
import { takeLogoutReason, type LogoutReason } from '@/auth/logoutReason'
import { returnPath } from '@/auth/returnPath'

const LOGOUT_NOTICE: Record<LogoutReason, string> = {
  idle: 'ההתחברות הסתיימה לאחר זמן ללא פעילות. נא להתחבר מחדש',
  expired: 'ההתחברות הסתיימה כי חלף משך ההתחברות המרבי. נא להתחבר מחדש',
  ended: 'ההתחברות הסתיימה. נא להתחבר מחדש',
  password: 'ההתחברות הסתיימה כי הסיסמה שונתה. נא להתחבר מחדש עם הסיסמה החדשה',
}

const LoginPage: React.FC = () => {
  const { login, isLoading, error } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { setSelectedSite } = useSitesStore()
  const { mode, toggleTheme } = useAppTheme()
  const isDarkMode = mode === 'dark'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(false)
  const [logoutReason] = useState(takeLogoutReason)

  const hasError = Boolean(error)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const result = await login({ email, password })

    if (!result?.success) return

    if (result.requiresOtp) {
      navigate('/otp', { replace: true, state: location.state })
      return
    }

    if (result.requiresPasswordChange) {
      navigate('/change-password', { replace: true })
      return
    }

    const back = returnPath(location.state)
    if (back) {
      navigate(back, { replace: true })
      return
    }
    const dest = await resolvePostAuthDestination()
    if (dest.site) setSelectedSite(dest.site)
    navigate(dest.path, { replace: true })
  }

  // Theme-aware colors
  const colors = {
    background: isDarkMode ? '#0a1628' : '#F5FAFF',
    titleText: isDarkMode ? '#BBD7FF' : '#0B4F6C',
    welcomeText: isDarkMode ? '#7CAD44' : '#003A5D',
    subtitleText: isDarkMode ? '#63A4FF' : '#6B7A8C',
    languageText: isDarkMode ? '#BBD7FF' : '#4A5C6A',
    buttonBg: isDarkMode ? '#1e3a5f' : '#E3F5FF',
    buttonText: isDarkMode ? '#FFFFFF' : '#1D3981',
    buttonHover: isDarkMode ? '#2d4a6f' : '#B3E0FF',
    checkboxColor: isDarkMode ? '#94a3b8' : '#E5E5E5',
    inputBorderDefault: isDarkMode ? '#BBD7FF' : '#E0E3E7',
    inputBorderFocus: isDarkMode ? '#7CAD44' : '#4CAF50',
    inputBg: isDarkMode ? '#0f1f35' : '#FFFFFF',
  }

  return (
    <Box
      sx={{
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        bgcolor: colors.background,
        transition: 'background-color 0.3s ease',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          px: 8,
          py: 2,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 520,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            height: '100%',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={Logo}
              sx={{ width: '72px', height: '80px', objectFit: 'cover', objectPosition: 'top', borderRadius: '50%' }}
            />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: colors.titleText,
                Left: '150px',
                transition: 'color 0.3s ease',
              }}
            >
              Rimonim Tech SmartNet
            </Typography>
          </Box>
          <Box
            component="img"
            src={isDarkMode ? LoginDarkMode : LoginLightMode}
            alt="Water consumption graph"
            sx={{
              width: '80%',
              height: '220px',
              // objectFit: 'contain', 
              // borderRadius: 1,
              marginTop: '65px',
              marginLeft: '150px',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              mt: 10,
              position: 'relative',
              right: '150px',
            }}
          >
            {[
              { label: 'פתוח', count: 3, color: '#8577E6', w: 584 },
              { label: 'בתהליך', count: 6, color: '#F79268', w: 484 },
              { label: 'בוצע', count: 32, color: '#46BE8B', w: 344 },
            ].map(card => (
              <Box
                key={card.label}
                sx={{
                  width: card.w,
                  height: 65,
                  backgroundColor: isDarkMode ? '#202B40' : '#FAFBFD',
                  borderRadius: '16px',
                  border: `1px solid ${isDarkMode ? '#374151' : 'rgba(50, 59, 119, 0.10)'}`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  justifyContent: 'flex-end',
                  padding: '16px',
                  filter: 'drop-shadow(0px 8px 20px rgba(13, 75, 120, 0.08))',
                }}
              >
                <Typography sx={{ color: '#8F98A5', fontFamily: 'Rubik', fontSize: 16, fontWeight: 300 }}>
                  {card.count}
                </Typography>
                <Box
                  sx={{
                    backgroundColor: card.color,
                    color: '#EFF2F7',
                    fontFamily: 'Rubik',
                    fontSize: 20,
                    fontWeight: 400,
                    borderRadius: '4.5px',
                    padding: '10px 28px',
                    lineHeight: '24px',
                  }}
                >
                  {card.label}
                </Box>
                <Typography sx={{ color: isDarkMode ? '#FFFFFF' : '#003F61', fontSize: 12 }}>
                  ◄
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              mt: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: colors.languageText, fontWeight: 500, alignItems: 'left', transition: 'color 0.3s ease' }}
            >
              HE
              <ThemeToggle
                isDarkMode={isDarkMode}
                onThemeToggle={toggleTheme}
              />
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          maxWidth: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 3, md: 8 },
          py: 4,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 420,
            direction: 'rtl',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              component="img"
              src={Logo}
              alt="Rimonim Tech Logo"
              sx={{ width: '140px', height: '155px', objectFit: 'cover', objectPosition: 'top' }}
            />
          </Box>

          <Typography
            variant="h2"
            sx={{ fontWeight: 700, mb: 1, color: colors.welcomeText, transition: 'color 0.3s ease' }}
          >
            ברוכים הבאים!
          </Typography>

          <Typography variant="body1" sx={{ mb: 4, color: colors.subtitleText, transition: 'color 0.3s ease' }}>
            נא להזין כתובת מייל וסיסמה לצורך כניסה למערכת
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}
          >
            <LoginInput
              label="שם משתמש"
              type="text"
              value={email}
              onChange={setEmail}
              hasError={hasError}
              borderColors={{
                default: colors.inputBorderDefault,
                hover: colors.inputBorderFocus,
                focus: colors.inputBorderFocus,
              }}
              backgroundColor={colors.inputBg}
              accentColor="#6DBB4C"
              accentWidth={20}
              isDarkMode={isDarkMode}
            />

            <LoginInput
              label="סיסמה"
              type="password"
              value={password}
              onChange={setPassword}
              hasError={hasError}
              borderColors={{
                default: colors.inputBorderDefault,
                hover: colors.inputBorderFocus,
                focus: colors.inputBorderFocus,
              }}
              backgroundColor={colors.inputBg}
              accentColor="#6DBB4C"
              accentWidth={20}
              isDarkMode={isDarkMode}
            />

            <FormControlLabel
              sx={{ alignItems: 'center' }}
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  sx={{ 
                    ml: 1, 
                    width: '1px', 
                    height: '1px', 
                    color: colors.checkboxColor,
                    '&.Mui-checked': {
                      color: '#72A4EB',
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{ color: colors.subtitleText, fontWeight: 500, transition: 'color 0.3s ease' }}
                >
                  השאר אותי מחובר/ת
                </Typography>
              }
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 2,
              }}
            >
              <Box
                sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}
              >
                {hasError && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#E53935', 
                      fontSize: '12px',
                      lineHeight: 1.4,
                      textAlign: 'right',
                    }}
                  >
                    פרטי ההתחברות שגויים
                    <br />
                    אם אתה זקוק לעזרה – פנה למנהל המערכת.
                  </Typography>
                )}
                {!hasError && logoutReason && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.subtitleText,
                      fontSize: '12px',
                      lineHeight: 1.4,
                      textAlign: 'right',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {LOGOUT_NOTICE[logoutReason]}
                  </Typography>
                )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  borderRadius: 1,
                  py: 1,
                  px: 4,
                  height: '35px',
                  width: '35%',
                  fontWeight: 400,
                  bgcolor: colors.buttonBg,
                  color: colors.buttonText,
                  boxShadow: '0px 1px 2px rgba(0, 130, 200, 0.25)',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.3s ease, color 0.3s ease',
                  '&:hover': {
                    bgcolor: colors.buttonHover,
                  },
                }}
              >
                {isLoading ? 'מתחבר...' : 'התחברות'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default LoginPage
